import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { NominationFilterKeys, TNominationFilterKey } from './NominationSidebarFilters';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';

export const transformNominationsSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TNominationFilterKey, string> => {
    let result: Record<TNominationFilterKey, string> = {} as Record<TNominationFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case NominationFilterKeys.siteId:
                result[key] = value
                    .map((val: number) => {
                        const option = context.siteOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.enrollmentId:
                result[key] = value
                    .map((val: string) => {
                        const option = context.meterEnrollmentEnrollmentIdOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.accountManager:
                result[key] = value
                    .map((val: string) => {
                        const option = context.accountManagerOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.iso:
                result[key] = value
                    .map((val: string) => {
                        const option = context.isoOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.utility:
                result[key] = value
                    .map((val: string) => {
                        const option = context.programUtilityOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.market:
                result[key] = value
                    .map((val: string) => {
                        const option = context.marketOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.program:
                result[key] = value
                    .map((val: string) => {
                        const option = context.programOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.controlProvider:
                result[key] = value
                    .map((val: string) => {
                        const option = context.controlProviderOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.enrollmentStatus:
                result[key] = value
                    .map((val: string) => {
                        const option = context.meterEnrollmentStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.nominationRecency:
                result[key] = value
                    .map((val: string) => {
                        const option = context.nominationRecencyOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case NominationFilterKeys.nominationStatus:
                result[key] = value
                    .map((val: string) => {
                        const option = context.nominationStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TNominationFilterKey, string>;
    }

    return result;
};

export const transformNominationsSavedFilterValuesFromDto = (filter: ISavedFilter['filter_values']): Record<TNominationFilterKey, unknown> => {
    const result: Record<TNominationFilterKey, unknown> = {} as Record<TNominationFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case NominationFilterKeys.siteId:
            case NominationFilterKeys.enrollmentId:
            case NominationFilterKeys.accountManager:
            case NominationFilterKeys.iso:
            case NominationFilterKeys.utility:
            case NominationFilterKeys.market:
            case NominationFilterKeys.program:
            case NominationFilterKeys.controlProvider:
            case NominationFilterKeys.enrollmentStatus:
            case NominationFilterKeys.nominationRecency:
            case NominationFilterKeys.nominationStatus:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformNominationsSavedFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TNominationFilterKey, unknown> => {
    const result: Record<TNominationFilterKey, unknown> = {} as Record<TNominationFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case NominationFilterKeys.siteId:
            case NominationFilterKeys.enrollmentId:
            case NominationFilterKeys.accountManager:
            case NominationFilterKeys.iso:
            case NominationFilterKeys.utility:
            case NominationFilterKeys.market:
            case NominationFilterKeys.program:
            case NominationFilterKeys.controlProvider:
            case NominationFilterKeys.enrollmentStatus:
            case NominationFilterKeys.nominationRecency:
            case NominationFilterKeys.nominationStatus:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case NominationFilterKeys.siteId:
                if (Array.isArray(value)) {
                    const validOptionValues = context.siteOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.enrollmentId:
                if (Array.isArray(value)) {
                    const validOptionValues = context.meterEnrollmentEnrollmentIdOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.accountManager:
                if (Array.isArray(value)) {
                    const validOptionValues = context.accountManagerOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.iso:
                if (Array.isArray(value)) {
                    const validOptionValues = context.isoOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.utility:
                if (Array.isArray(value)) {
                    const validOptionValues = context.programUtilityOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.market:
                if (Array.isArray(value)) {
                    const validOptionValues = context.marketOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.program:
                if (Array.isArray(value)) {
                    const validOptionValues = context.programOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.controlProvider:
                if (Array.isArray(value)) {
                    const validOptionValues = context.controlProviderOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.enrollmentStatus:
                if (Array.isArray(value)) {
                    const validOptionValues = context.meterEnrollmentStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case NominationFilterKeys.nominationRecency:
                if (Array.isArray(value)) {
                    const validOptionValues = context.nominationRecencyOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case NominationFilterKeys.nominationStatus:
                if (Array.isArray(value)) {
                    const validOptionValues = context.nominationStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TNominationFilterKey, string>): TNominationFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TNominationFilterKey[];
}
